import {
  getDefaultBlockState,
  isBlockAvailable
} from "../core/blockRegistry.ts";
import {
  placeBlockWithRules
} from "../core/blockPlacement.ts";
import type { VoxelWorld } from "../core/VoxelWorld.ts";

function firstAvailable(candidates: string[]): string | null {
  return candidates.find((blockId) => isBlockAvailable(blockId)) ?? null;
}

export function generateSpecialRendererShowcase(
  world: VoxelWorld
): number {
  const floorBlock =
    firstAvailable([
      "polished_andesite",
      "stone_bricks",
      "stone",
      "cobblestone"
    ]) ?? firstAvailable(Array.from(world.blocks.values()));

  if (!floorBlock) {
    return 0;
  }

  world.beginTransaction("Generate special renderer showcase");

  try {
    world.runBatch(() => {
      world.fill(-12, 0, -7, 12, 0, 8, floorBlock, {});

      const chestCandidates = [
        "chest",
        "trapped_chest",
        "ender_chest",
        "copper_chest",
        "exposed_copper_chest",
        "weathered_copper_chest",
        "oxidized_copper_chest"
      ].filter((blockId) => isBlockAvailable(blockId));

      chestCandidates.slice(0, 7).forEach((blockId, index) => {
        const x = -10 + index * 3;
        world.setBlock(
          x,
          1,
          -4,
          blockId,
          {
            ...getDefaultBlockState(blockId),
            facing: index % 2 === 0 ? "south" : "east",
            type: "single"
          }
        );

        if (index === 0) {
          world.setBlockEntityData(x, 1, -4, {
            container: {
              customName: "Voxel Studio Supplies",
              items: [
                {
                  slot: 0,
                  id: "minecraft:diamond",
                  count: 12
                },
                {
                  slot: 1,
                  id: "minecraft:lantern",
                  count: 8
                }
              ]
            }
          });
        }
      });

      const bannerCandidates = [
        "red_banner",
        "blue_banner",
        "white_banner",
        "black_banner"
      ].filter((blockId) => isBlockAvailable(blockId));

      bannerCandidates.forEach((blockId, index) => {
        const x = -9 + index * 3;
        world.setBlock(
          x,
          1,
          1,
          blockId,
          {
            ...getDefaultBlockState(blockId),
            rotation: String((index * 4) % 16)
          }
        );
        world.setBlockEntityData(x, 1, 1, {
          banner: {
            patterns: [
              {
                pattern: "minecraft:stripe_bottom",
                color: index % 2 === 0 ? "white" : "black"
              },
              {
                pattern: "minecraft:cross",
                color: index % 2 === 0 ? "yellow" : "light_blue"
              }
            ]
          }
        });
      });

      const wallBanner = firstAvailable([
        "purple_wall_banner",
        "red_wall_banner",
        "white_wall_banner"
      ]);
      if (wallBanner) {
        world.setBlock(
          5,
          2,
          1,
          wallBanner,
          {
            ...getDefaultBlockState(wallBanner),
            facing: "south"
          }
        );
      }

      const shulkers = [
        "white_shulker_box",
        "red_shulker_box",
        "blue_shulker_box",
        "shulker_box"
      ].filter((blockId) => isBlockAvailable(blockId));

      shulkers.forEach((blockId, index) => {
        world.setBlock(
          -9 + index * 3,
          1,
          5,
          blockId,
          {
            ...getDefaultBlockState(blockId),
            facing: "up"
          }
        );
      });

      const cushions = [
        "red_cushion",
        "blue_cushion",
        "green_cushion",
        "yellow_cushion"
      ].filter((blockId) => isBlockAvailable(blockId));

      cushions.forEach((blockId, index) => {
        world.setBlock(
          4 + index * 2,
          1,
          5,
          blockId,
          getDefaultBlockState(blockId)
        );
      });

      const sign = firstAvailable([
        "poplar_sign",
        "oak_sign",
        "spruce_sign"
      ]);
      if (sign) {
        world.setBlock(
          8,
          1,
          1,
          sign,
          {
            ...getDefaultBlockState(sign),
            rotation: "8"
          }
        );
        world.setBlockEntityData(8, 1, 1, {
          sign: {
            front: {
              lines: ["Voxel", "Studio", "Block", "Entity"],
              color: "blue",
              glowing: true
            },
            back: {
              lines: ["Java", String(world.minecraftVersion || ""), "", ""],
              color: "black",
              glowing: false
            },
            waxed: true
          }
        });
      }

      const hangingSign = firstAvailable([
        "poplar_hanging_sign",
        "oak_hanging_sign",
        "spruce_hanging_sign"
      ]);
      if (hangingSign) {
        world.setBlock(
          10,
          2,
          1,
          hangingSign,
          {
            ...getDefaultBlockState(hangingSign),
            rotation: "8",
            attached: "false"
          }
        );
        world.setBlockEntityData(10, 2, 1, {
          sign: {
            front: {
              lines: ["Official", "Version", "Assets", "✓"],
              color: "green",
              glowing: false
            },
            back: {
              lines: ["", "", "", ""],
              color: "black",
              glowing: false
            },
            waxed: false
          }
        });
      }

      const bed = firstAvailable([
        "red_bed",
        "blue_bed",
        "white_bed",
        "straw_bed"
      ]);
      if (bed) {
        placeBlockWithRules(
          world,
          { x: 7, y: 1, z: -1 },
          bed,
          {
            ...getDefaultBlockState(bed),
            facing: "east"
          }
        );
      }
    });

    return world.commitTransaction();
  } catch (error) {
    world.cancelTransaction();
    throw error;
  }
}
